import React, {useEffect, useMemo, useRef, useState} from "react";
import {NewItemWrap, Checkbox} from "components/atoms/MenuManagement/AddNewItem/AddNewItem.styles";
import Form from "components/molecules/Form/Form";
import Field from "components/molecules/Field";
import {useForm} from "components/molecules/Form";
import Button from "components/molecules/Button";
import {FaPlus, FaRegEdit} from "react-icons/fa";
import {IoMdArrowDropdown} from "react-icons/io";
import AddVariation from "components/atoms/MenuManagement/AddVariation/Index";
import ModalContainer from "components/molecules/ModalContainer";
import ChoiceModal from "components/atoms/MenuManagement/ChoiceModal";
import SwitchToggle from "components/atoms/Menu/Menu/SwitchToggle";
import menuStoreService from "services/menu-storeService";
import {useAuthContext} from "Context/authContext";
import Toast from "components/molecules/Toast";
import {useSelector} from "react-redux";
import CenterModal from "components/molecules/Modal/CenterModal";
import ItemPreviewModal from "../ItemPreviewModal";
import AddIngredients from "../AddIngredients/Index";
import DualImageUpload from "components/molecules/DualImageUpload";

const AddNewItem = ({menuId, btnText, onClose, isEdit, item, categoryId, setPreviewModal, setPayload, currentStoreId}) => {
    const [form] = useForm();
    const [isOn, setIsOn] = useState(false);
    const [selectedOption, setSelectedOption] = useState("Available");
    const [status, setStatus] = useState(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownOptions = ["Available", "Unavailable"];
    const [variations, setVariations] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [groupChoices, setGroupChoices] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [verticalImage, setVerticalImage] = useState(null);
    const [horizontalImage, setHorizontalImage] = useState(null);
    const defaultStore = useSelector(state => state.business.defaultStore);
    const {refetch} = useAuthContext();

    // Use currentStoreId if provided, otherwise fallback to defaultStore
    const storeId = currentStoreId || defaultStore;

    // Local refresh state to force category refresh without global refetch
    const [localRefresh, setLocalRefresh] = useState(0);
    const {categories_data} = menuStoreService.GetAllCategories(menuId || "", localRefresh);
    const {choice_group_data} = menuStoreService.GetAllChoiceGroups(storeId, "");

    const [loading, setLoading] = useState(false);

    // Debug logging (can be removed in production)

    const categories = useMemo(() => {
        if (!categories_data) {
            return [];
        }
        return categories_data.map(_ => ({
            value: _._id,
            label: _.name,
        }));
    }, [categories_data]);

    useEffect(() => {
        if (!item) return;
        
        // Debug: Log the item structure to see ingredients data
        console.log("Edit item data:", item);
        console.log("Item ingredients:", item?.defaultIngredients);
        console.log("Item ingredients (alt):", item?.ingredients);
        
        // Set base fields immediately
        form.setFieldsValue({
            name: item?.name,
            description: item?.description,
            calories: item?.calories,
            price: item?.price,
            offer: item?.discountPrice,
        });
        
        // Set existing images for edit mode
        if (item?.verticalImage) {
            setVerticalImage(item.verticalImage);
        } else if (item?.itemImage) {
            // Backward compatibility: use old itemImage as vertical if new images don't exist
            setVerticalImage(item.itemImage);
        }
        
        if (item?.horizontalImage) {
            setHorizontalImage(item.horizontalImage);
        }

        // Resolve and set category when options are available
        if (categories && categories.length > 0) {
            const itemCategoryId = categoryId || item?.category?._id || item?.category || item?.categoryId;
            const selectedCategory = categories.find(opt => opt.value === itemCategoryId);
            if (selectedCategory) {
                form.setFieldsValue({category: selectedCategory});
            }
        }

        setVariations(item?.variations || []);
        
        // Set ingredients - handle new nested structure
        const itemIngredients = item?.defaultIngredients || [];
        console.log("Setting ingredients (new structure):", itemIngredients);
        setIngredients(itemIngredients);
        
        setGroupChoices(choice_group_data?.data || []);

        // ✅ Pre-select groups if editing
        if (item?.groupChoices?.length > 0) {
            setSelectedGroups(item.groupChoices.map(g => g._id || g));
        }

        if (item?.availability) {
            setSelectedOption(dropdownOptions[0]);
            setStatus(true);
        } else {
            setSelectedOption(dropdownOptions[1]);
            setStatus(false);
        }
        if (item?.discountPrice) {
            setIsOn(true);
        }
    }, [item, categories, choice_group_data]);

    // Force refresh categories when modal opens with a new categoryId
    useEffect(() => {
        if (categoryId) {
            setLocalRefresh(prev => prev + 1);
        }
    }, [categoryId]);

    const handleOptionClick = option => {
        setSelectedOption(option);
        setStatus(option === "Available");
        setIsDropdownOpen(false);
    };

    const handleToggle = () => {
        setIsOn(prev => !prev);
        if (isOn) {
            form.setFieldsValue({offer: 0});
        }
    };

    const DropdownRef = useRef(null);
    const handleClickOutside = event => {
        if (DropdownRef.current && !DropdownRef.current.contains(event.target)) {
            setIsDropdownOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // ✅ toggle group selection
    const handleGroupToggle = id => {
        setSelectedGroups(prev => {
            if (prev.includes(id)) {
                return prev.filter(groupId => groupId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleSubmit = async data => {
        try {
            // Validate that at least one image is uploaded
            if (!verticalImage && !horizontalImage) {
                Toast({
                    type: "error",
                    message: "Please upload at least one image (vertical or horizontal)"
                });
                return;
            }
            
            // setLoading(true);
            const payload = new FormData();
            payload.append("name", data?.name);
            payload.append("description", data?.description);
            payload.append("calories", data?.calories);
            
            // Append dual images - only send new files (File objects)
            // If images are strings (URLs), they're existing images - backend will preserve them
            if (verticalImage && typeof verticalImage !== 'string') {
                payload.append("verticalImage", verticalImage);
            }
            if (horizontalImage && typeof horizontalImage !== 'string') {
                payload.append("horizontalImage", horizontalImage);
            }
            
            payload.append("price", data?.price);
            payload.append("discountPrice", data?.offer || 0);
            payload.append("category", data?.category?.value);
            payload.append("menuId", menuId);
            payload.append("availability", status);
            payload.append("variations", JSON.stringify(variations));
            payload.append("branchId", storeId);
            console.log("ingredients", ingredients);
            payload.append("defaultIngredients", JSON.stringify(ingredients))

            // ✅ send only checked groups
            payload.append("groupChoices", JSON.stringify(selectedGroups));

            if (!isEdit) {
                setPayload(payload);
                setPreviewModal(true);
            } else {
                try {
                    let res;
                    if (!isEdit) {
                        res = await menuStoreService.createItem(payload);
                    } else {
                        res = await menuStoreService.updateItem(payload, item?._id);
                    }

                    Toast({type: "success", message: res?.message});
                    refetch();
                } catch (error) {
                    Toast({type: "error", message: "Failed"});
                }
            }

            // setLoading(false);
            onClose();
        } catch (error) {
            setLoading(false);
            Toast({type: "error", message: error?.message});
        }
    };

    // Show message if no menu is selected
    if (!menuId) {
        return (
            <div style={{padding: "20px", textAlign: "center"}}>
                <p>Please select a menu first to add items.</p>
                <Button onClick={onClose}>Close</Button>
            </div>
        );
    }

    return (
        <NewItemWrap>
            <Form form={form} onSubmit={handleSubmit} className="form-wrap">
                {/* --- Availability Dropdown --- */}
                <div className="Add-item-header">
                    <div className={`dropdownWrap ${selectedOption.toLocaleLowerCase()}`}>
                        <div className="modal-dropdown" ref={DropdownRef}>
                            <div className={`dot ${selectedOption.toLocaleLowerCase()}`}></div>
                            <div className="dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                <p className={`${selectedOption.toLocaleLowerCase()}`}>{selectedOption}</p>
                                <IoMdArrowDropdown size={20} style={{color: "grey"}} />
                            </div>
                            {isDropdownOpen && (
                                <ul className="styled-select">
                                    {dropdownOptions.map(option => (
                                        <li
                                            className={`${option.toLocaleLowerCase()}`}
                                            key={option}
                                            onClick={() => handleOptionClick(option)}>
                                            {option}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Dual Image Upload --- */}
                <DualImageUpload
                    verticalImage={verticalImage}
                    horizontalImage={horizontalImage}
                    onVerticalChange={setVerticalImage}
                    onHorizontalChange={setHorizontalImage}
                    maxFileSize={2}
                    accept="image/jpeg, image/jpg, image/png"
                />

                {/* --- Basic Fields --- */}
                <div className="fieldsWrap">
                    <div className="user-fields">
                        <Form.Item
                            label="Item Name *"
                            type="name"
                            rounded
                            name="name"
                            lg
                            placeholder="enter product title"
                            $bgWhite
                            rules={[{required: true, message: "Item name is required"}]}>
                            <Field />
                        </Form.Item>
                        <Form.Item
                            label="Item Description *"
                            type="name"
                            rounded
                            name="description"
                            lg
                            placeholder="enter description "
                            $bgWhite
                            rules={[{required: true, message: "Item description is required"}]}>
                            <Field />
                        </Form.Item>
                    </div>
                    <div className="user-fields">
                        <Form.Item
                            label="Select Category *"
                            type="select"
                            rounded
                            name="category"
                            lg
                            placeholder="Select category"
                            $bgWhite
                            options={categories}
                            rules={[{required: true, message: "Category is required"}]}>
                            <Field />
                        </Form.Item>
                        <Form.Item
                            label="Calories (per serving)"
                            type="number"
                            min={0}
                            step="any"
                            rounded
                            name="calories"
                            lg
                            placeholder="e.g., 250 or 250.5"
                            $bgWhite>
                            <Field suffix="kcal" />
                        </Form.Item>
                    </div>
                </div>

                {/* --- Price & Offer --- */}
                <div className="user-fields fieldsWrap">
                    <div className="offers">
                        <strong>Price & Offer</strong>
                        <p>Add Price & an Offer for the item to show your customers.</p>
                        <div className="user-fields">
                            <div className="swtichToggle">
                                <SwitchToggle onChange={handleToggle} checked={isOn} />
                            </div>
                            <Form.Item
                                label="Price *"
                                type="number"
                                min={0}
                                step="any"
                                rounded
                                name="price"
                                lg
                                placeholder="$ 20 or 19.55"
                                $bgWhite
                                rules={[{required: true, message: "Item price is required"}]}>
                                <Field />
                            </Form.Item>
                            <Form.Item
                                label="Offer"
                                type="number"
                                min={0}
                                step="any"
                                rounded
                                name="offer"
                                lg
                                placeholder="$ 15 or 14.99"
                                $bgWhite
                                disabled={!isOn && !item?.discountPrice}>
                                <Field />
                            </Form.Item>
                        </div>
                    </div>

                    {/* --- Variations --- */}
                    <div className="offers">
                        <strong>Variations</strong>
                        <p>Add a variation of this item comes in sizes (e.g. Small, Med...)</p>
                        <div className="variations">
                            <strong>{`"${variations.length || 0}"`} variation added</strong>
                            <ModalContainer
                                width={850}
                                noBorder
                                title="Edit Variation"
                                btnComponent={({onClick}) => (
                                    <Button sm onClick={onClick} type="button" disabled={variations.length === 0}>
                                        <FaRegEdit /> Edit Variation
                                    </Button>
                                )}
                                content={({onClose}) => (
                                    <AddVariation
                                        btnText="Save Items"
                                        isEdit={true}
                                        setVariations={setVariations}
                                        variationsData={variations}
                                        onClose={onClose}
                                    />
                                )}
                            />
                        </div>
                        <div className="btn-wrap">
                            <ModalContainer
                                width={850}
                                noBorder
                                title="Add Variation"
                                btnComponent={({onClick}) => (
                                    <Button disabled={variations?.length > 0} gray sm onClick={onClick} type="button">
                                        <FaPlus /> Add Variation
                                    </Button>
                                )}
                                content={({onClose}) => (
                                    <AddVariation setVariations={setVariations} onClose={onClose} />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="user-fields fieldsWrap">
                    <div className="offers">
                        <strong>Default Ingredients</strong>
                        <p>Customers can uncheck removable items when ordering.</p>
                    </div>

                    {/* --- Variations --- */}
                    <div className="offers ingredients-wrap">
                        <div className="variations">
                            <strong>{`"${ingredients.reduce((total, group) => total + (group.ingredients?.length || 0), 0) || 0}"`} Ingredients added</strong>
                            <ModalContainer
                                width={850}
                                noBorder
                                title="Edit Ingredient"
                                btnComponent={({onClick}) => (
                                    <Button sm onClick={onClick} type="button" disabled={ingredients.reduce((total, group) => total + (group.ingredients?.length || 0), 0) === 0}>
                                        <FaRegEdit /> Edit Ingredients
                                    </Button>
                                )}
                                content={({onClose}) => (
                                    <AddIngredients
                                        btnText="Save Items"
                                        isEdit={true}
                                        setIngredients={setIngredients}
                                        ingredientsData={ingredients}
                                        onClose={onClose}
                                    />
                                )}
                            />
                        </div>
                        <div className="btn-wrap">
                            <ModalContainer
                                width={850}
                                noBorder
                                title="Add Ingredient"
                                btnComponent={({onClick}) => (
                                    <Button disabled={ingredients.reduce((total, group) => total + (group.ingredients?.length || 0), 0) > 0} gray sm onClick={onClick} type="button">
                                        <FaPlus /> Add Ingredient
                                    </Button>
                                )}
                                content={({onClose}) => (
                                    <AddIngredients setIngredients={setIngredients} onClose={onClose} />
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* --- Choice Groups --- */}
                <div className="choice-group">
                    <div className="text">
                        <strong>Choice Groups</strong>
                        <p>Add Choices for this item that customers can choose.</p>
                    </div>
                    <div className="create-choice-btn">
                        <ModalContainer
                            width={850}
                            noBorder
                            title="Create Choice Group"
                            btnComponent={({onClick}) => (
                                <Button sm onClick={onClick} type="button">
                                    <FaPlus /> Create Choice Group
                                </Button>
                            )}
                            content={({onClose}) => (
                                <ChoiceModal
                                    setGroupChoices={setGroupChoices}
                                    onClose={onClose}
                                    groupChoices={groupChoices}
                                />
                            )}
                        />
                    </div>
                </div>

                {groupChoices.length > 0 &&
                    groupChoices.map((choice, ind) => (
                        <div className="choice-wrapper" key={choice?._id || ind}>
                            <div className="choice">
                                <Checkbox
                                    type="checkbox"
                                    checked={selectedGroups.includes(choice?._id)}
                                    onChange={() => handleGroupToggle(choice?._id)}
                                />
                                <div className="inner-text">
                                    <strong>{choice?.groupName}</strong>
                                    <p>
                                        Required (Selected {choice?.minChoice}), {choice?.maxChoice} Choices
                                    </p>
                                </div>
                            </div>
                            <div className="choice">
                                <strong>{choice?.availableChoices}</strong>
                            </div>
                            <ModalContainer
                                width={850}
                                noBorder
                                title="Create Choice Group"
                                btnComponent={({onClick}) => (
                                    <Button sm type="button" onClick={onClick}>
                                        <FaRegEdit /> Edit
                                    </Button>
                                )}
                                content={({onClose}) => (
                                    <ChoiceModal
                                        editChoice={true}
                                        index={ind}
                                        groupData={choice}
                                        setGroupChoices={setGroupChoices}
                                        onClose={onClose}
                                        groupChoices={groupChoices}
                                    />
                                )}
                            />
                        </div>
                    ))}

                {/* --- Submit Button --- */}
                <div className="btn-holder">
                    <Button lg type="submit" loader={loading}>
                        {btnText}
                    </Button>
                </div>
            </Form>
        </NewItemWrap>
    );
};

export default AddNewItem;
